import { defineStore } from 'pinia'
import { ROLES, type UserRole, isAdminFamilyRole } from '../constants/roles'

type User = {
  id: number
  username: string
  email: string
  phone?: string
  role: UserRole
  is_active: boolean
  school_id?: string
  district?: string
  rd_block?: string
  created_at: string
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    isAuthenticated: false as boolean,
    currentUser: null as User | null,
    token: null as string | null,
    csrf: null as string | null,
  }),

  getters: {
    isLoggedIn(state): boolean {
      // SECURITY: Access tokens are now in HTTP-only cookies (not accessible to JavaScript)
      // Check authentication status from state or localStorage flag only
      return state.isAuthenticated || localStorage.getItem('isAuthenticated') === 'true'
    },
    
    // Role-based getters
    isSuperAdmin(state): boolean {
      return state.currentUser?.role === ROLES.SUPER_ADMIN
    },
    
    isMinister(state): boolean {
      return state.currentUser?.role === ROLES.MINISTER
    },
    
    isAdmin(state): boolean {
      return isAdminFamilyRole(state.currentUser?.role)
    },
    
    isDEO(state): boolean {
      return state.currentUser?.role === ROLES.DEO
    },
    
    isSDEO(state): boolean {
      return state.currentUser?.role === ROLES.SDEO
    },
    
    isHOI(state): boolean {
      return state.currentUser?.role === ROLES.HOI
    },
    
    isTeacher(state): boolean {
      return state.currentUser?.role === ROLES.TEACHER
    },
    
    userRole(state): UserRole | null {
      return state.currentUser?.role || null
    }
  },

  actions: {
    initializeFromStorage(): void {
      // SECURITY: Access tokens are now in HTTP-only cookies (not accessible to JavaScript)
      // Only restore user info and authentication state, not tokens
      const stored = localStorage.getItem('isAuthenticated')
      const userStr = localStorage.getItem('currentUser')
      const csrf = localStorage.getItem('csrf')
      if (stored === 'true' && userStr) {
        this.isAuthenticated = true
        this.currentUser = JSON.parse(userStr)
        this.token = null // Token is in HTTP-only cookie, not accessible
        this.csrf = csrf
      }
    },

    async login(username: string, password: string): Promise<boolean> {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // SECURITY: Required for HTTP-only cookies
        body: JSON.stringify({ username, password }),
      })
      const data = await response.json()
      if (data?.success && data?.data?.user) {
        this.isAuthenticated = true
        this.currentUser = data.data.user
        // SECURITY: Access token is now in HTTP-only cookie (not accessible to JavaScript)
        this.token = null // Don't store token in state or localStorage
        
        // SECURITY: Clean up any old tokens from localStorage (from before cookie migration)
        localStorage.removeItem('token')
        localStorage.removeItem('authToken')
        localStorage.removeItem('teacherToken')
        
        this.csrf = data.data.csrf || null
        localStorage.setItem('isAuthenticated', 'true')
        localStorage.setItem('currentUser', JSON.stringify(this.currentUser))
        // Don't store token in localStorage - it's in HTTP-only cookie
        if (this.csrf) localStorage.setItem('csrf', this.csrf)
        return true
      }
      return false
    },

    setUser(user: User): void {
      this.currentUser = user
      localStorage.setItem('currentUser', JSON.stringify(user))
    },

    setAuthenticated(authenticated: boolean): void {
      this.isAuthenticated = authenticated
      localStorage.setItem('isAuthenticated', authenticated.toString())
    },

    setToken(token: string): void {
      // SECURITY: Tokens are now in HTTP-only cookies, not stored in state/localStorage
      // This method is kept for backward compatibility but does nothing
      this.token = null
    },

    /**
     * Clear auth state locally without calling logout API.
     * Use when session is invalid (e.g. token expired) and we need to redirect to login.
     */
    clearAuthState(): void {
      this.isAuthenticated = false
      this.currentUser = null
      this.token = null
      this.csrf = null
      localStorage.removeItem('isAuthenticated')
      localStorage.removeItem('currentUser')
      localStorage.removeItem('csrf')
      localStorage.removeItem('token')
      localStorage.removeItem('authToken')
      localStorage.removeItem('teacherToken')
    },

    async logout(): Promise<void> {
      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          credentials: 'include' // SECURITY: Required to clear HTTP-only cookies
        })
      } catch {}
      this.clearAuthState()
    }
  }
})


